/**
 * Typed HTTP client for the frozen REST surface of docs/CONTRACT.md §4.
 *
 * Runtime-free: it uses only `fetch`, `Headers`, `FormData` and `URLSearchParams`, so the same
 * class works in the browser, in a service worker and under Bun. The bearer device token is the
 * only credential; nothing else is sent.
 */
import type {
  AdminMembersResponse,
  AdminStatsView,
  AdminUserPatchRequest,
  AdminUsersResponse,
  AddChatMembersRequest,
  AddChatMembersResponse,
  ApiErrorCode,
  AuthFailureCode,
  AuthResponse,
  CallDetailResponse,
  CallLogResponse,
  CallTokenResponse,
  CallsResponse,
  EditMessageRequest,
  ExportResponse,
  ForwardMessagesRequest,
  ForwardMessagesResponse,
  ChangePasswordRequest,
  ChatDetailResponse,
  ChatResponse,
  ChatsResponse,
  CreateChatRequest,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateRecoveryRequest,
  CreateRecoveryResponse,
  DevicesResponse,
  HealthView,
  HttpMethod,
  InviteAuthRequest,
  InvitesResponse,
  LoginRequest,
  MeResponse,
  MediaResponse,
  MessageResponse,
  MessagesQuery,
  MessagesResponse,
  OkResponse,
  PatchChatRequest,
  PatchChatMuteRequest,
  PatchMeRequest,
  PostMessageRequest,
  PushSubscribeRequest,
  PushUnsubscribeRequest,
  PrivacyRequest,
  ReadRequest,
  SearchQuery,
  SearchResponse,
  SetReactionRequest,
  VoteRequest,
  RingCallRequest,
  SignupRequest,
  StartCallRequest,
  UserResponse,
  UserStatus,
  UsersResponse,
} from "./types";

/** Contract error codes plus the two failures that never reach the server. */
export type ApiClientErrorCode =
  | ApiErrorCode
  | AuthFailureCode
  | "not_approved"
  | "network"
  | "internal";

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ApiClientErrorCode;
  /**
   * The §11 gate's `status` field, when the response carried one — pending versus blocked without
   * a second call. Null for every other failure.
   */
  readonly userStatus: UserStatus | null;

  constructor(status: number, code: ApiClientErrorCode, message: string, userStatus: UserStatus | null = null) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.userStatus = userStatus;
  }
}

export type ApiQueryValue = string | number | boolean | null | undefined;

export type ApiRequestInit = {
  method?: HttpMethod;
  query?: Record<string, ApiQueryValue>;
  /** Serialized as an `application/json` body. */
  json?: unknown;
  /** Sent as `multipart/form-data`; mutually exclusive with `json`. */
  form?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Minimal call signature that both the DOM `fetch` and Bun's `fetch` (which adds `preconnect`)
 * satisfy. Typing this as the global `typeof fetch` breaks under the api's `types: ["bun"]`,
 * where the fallback arrow in the constructor has no `preconnect` to offer.
 */
export type FetchLike = (input: string | URL | Request, init?: RequestInit) => Promise<Response>;

export type ApiClientOptions = {
  /** Origin of the api, e.g. `https://olmt-call-api.localhost`. Empty means same-origin. */
  baseUrl?: string;
  /** Device token; can be swapped later with `setToken`. */
  token?: string | null;
  /** Test seam / alternate fetch implementation. */
  fetchImpl?: FetchLike;
};

const API_ERROR_CODES: Record<ApiErrorCode, true> = {
  unauthorized: true,
  forbidden: true,
  not_found: true,
  bad_request: true,
  rate_limited: true,
  too_large: true,
  storage_full: true,
  conflict: true,
};

/** Type guard: narrows an untrusted `error.code` string to the contract's closed set. */
const isApiErrorCode = (value: unknown): value is ApiErrorCode =>
  typeof value === "string" && value in API_ERROR_CODES;

/** §4/§11: the codes that arrive as a flat `error` string rather than the envelope. */
const FLAT_ERROR_CODES: Record<AuthFailureCode | "not_approved", true> = {
  email_taken: true,
  bad_credentials: true,
  not_approved: true,
};

const FLAT_ERROR_MESSAGES: Record<AuthFailureCode | "not_approved", string> = {
  email_taken: "email already registered",
  bad_credentials: "unknown email or wrong password",
  not_approved: "this account is not approved yet",
};

/** Type guard: narrows an untrusted `error` string to the flat-body codes. */
const isFlatErrorCode = (value: unknown): value is AuthFailureCode | "not_approved" =>
  typeof value === "string" && value in FLAT_ERROR_CODES;

const USER_STATUSES: Record<UserStatus, true> = {
  pending: true,
  approved: true,
  rejected: true,
  suspended: true,
};

/** Type guard for the §11 `status` field of a gate response. */
export const isUserStatus = (value: unknown): value is UserStatus =>
  typeof value === "string" && value in USER_STATUSES;

export class ApiClient {
  #baseUrl: string;
  #token: string | null;
  #fetch: FetchLike;

  constructor(options: ApiClientOptions = {}) {
    this.#baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this.#token = options.token ?? null;
    // `this.#fetch(...)` invokes the function with `this` bound to this instance, which native
    // `fetch` rejects with "Illegal invocation" in browsers. Always call it off `globalThis`.
    this.#fetch = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  get baseUrl(): string {
    return this.#baseUrl;
  }

  get token(): string | null {
    return this.#token;
  }

  setToken(token: string | null): void {
    this.#token = token;
  }

  /** Absolute URL for an api path. */
  resolve(path: string): string {
    return `${this.#baseUrl}${path}`;
  }

  mediaUrl(mediaId: string): string {
    return this.resolve(`/api/media/${encodeURIComponent(mediaId)}`);
  }

  mediaThumbUrl(mediaId: string): string {
    return this.resolve(`/api/media/${encodeURIComponent(mediaId)}/thumb`);
  }

  /** `wss://…/api/ws` — pass this to `ChatSocket`, which appends the token. */
  wsUrl(): string {
    let base = this.#baseUrl;
    if (!base) {
      const scope: unknown = globalThis;
      if (typeof scope === "object" && scope !== null && "location" in scope) {
        const loc = scope.location;
        if (
          typeof loc === "object" &&
          loc !== null &&
          "origin" in loc &&
          typeof loc.origin === "string"
        ) {
          base = loc.origin;
        }
      }
    }
    return `${base.replace(/^http/, "ws")}/api/ws`;
  }

  /**
   * Sends one request and returns the raw `Response` (no throw on non-2xx). Use this for media
   * bytes, `Range` requests and anything that is not JSON.
   */
  async fetchRaw(path: string, init: ApiRequestInit = {}): Promise<Response> {
    const headers = new Headers();
    if (this.#token) headers.set("authorization", `Bearer ${this.#token}`);
    for (const [key, value] of Object.entries(init.headers ?? {})) headers.set(key, value);

    let body: string | FormData | undefined;
    if (init.json !== undefined) {
      headers.set("content-type", "application/json");
      body = JSON.stringify(init.json);
    } else if (init.form) {
      body = init.form;
    }

    let query = "";
    if (init.query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(init.query)) {
        if (value === undefined || value === null) continue;
        params.set(key, String(value));
      }
      const encoded = params.toString();
      if (encoded) query = `?${encoded}`;
    }

    const requestInit: RequestInit = { method: init.method ?? "GET", headers };
    if (body !== undefined) requestInit.body = body;
    if (init.signal) requestInit.signal = init.signal;

    try {
      return await this.#fetch(`${this.#baseUrl}${path}${query}`, requestInit);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new ApiClientError(0, "network", message);
    }
  }

  /** Sends one request, throws `ApiClientError` on non-2xx, and parses the JSON body. */
  async request<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
    const response = await this.fetchRaw(path, init);
    if (!response.ok) throw await this.#toError(response);
    const text = await response.text();
    if (!text) return {} as T;
    return JSON.parse(text) as T;
  }

  async #toError(response: Response): Promise<ApiClientError> {
    let code: ApiClientErrorCode = "internal";
    let message = `HTTP ${response.status}`;
    let userStatus: UserStatus | null = null;
    const text = await response.text().catch(() => "");
    if (text) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (typeof parsed === "object" && parsed !== null && "error" in parsed) {
          const error: unknown = parsed.error;
          if (typeof error === "string") {
            // §4/§11: a flat code — the auth failures and the approval gate.
            if (isFlatErrorCode(error)) {
              code = error;
              message = FLAT_ERROR_MESSAGES[error];
            }
            if ("status" in parsed && isUserStatus(parsed.status)) userStatus = parsed.status;
          } else if (typeof error === "object" && error !== null) {
            if ("message" in error && typeof error.message === "string") message = error.message;
            if ("code" in error && isApiErrorCode(error.code)) code = error.code;
          }
        }
      } catch {
        /* non-JSON body: keep the HTTP default */
      }
    }
    return new ApiClientError(response.status, code, message, userStatus);
  }

  /** Multipart upload of one `file` part. */
  async #upload<T>(path: string, file: Blob, filename?: string): Promise<T> {
    const form = new FormData();
    const name = filename ?? (file instanceof File ? file.name : "upload");
    form.append("file", file, name || "upload");
    return this.request<T>(path, { method: "POST", form });
  }

  /* --------------------------------------------------------------- health */

  health(): Promise<HealthView> {
    return this.request<HealthView>("/api/health");
  }

  /* ----------------------------------------------------------- auth / me */

  /** §4: open sign-up. The returned token already works for `GET /api/me`; the API waits for approval. */
  signup(input: SignupRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>("/api/auth/signup", { method: "POST", json: input });
  }

  /** §4: sign in an existing user and add this device. */
  login(input: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>("/api/auth/login", { method: "POST", json: input });
  }

  redeemInvite(input: InviteAuthRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>("/api/auth/invite", { method: "POST", json: input });
  }

  me(): Promise<MeResponse> {
    return this.request<MeResponse>("/api/me");
  }

  patchMe(input: PatchMeRequest): Promise<UserResponse> {
    return this.request<UserResponse>("/api/me", { method: "PATCH", json: input });
  }

  /**
   * §4: change the caller's own password. The current one is verified server-side against the
   * stored argon2id hash; a wrong one is `401 bad_credentials`, the flat body login uses.
   */
  changePassword(input: ChangePasswordRequest): Promise<OkResponse> {
    return this.request<OkResponse>("/api/me/password", { method: "PATCH", json: input });
  }

  uploadAvatar(file: Blob, filename?: string): Promise<UserResponse> {
    return this.#upload<UserResponse>("/api/me/avatar", file, filename);
  }

  devices(): Promise<DevicesResponse> {
    return this.request<DevicesResponse>("/api/me/devices");
  }

  deleteDevice(deviceId: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/me/devices/${encodeURIComponent(deviceId)}`, {
      method: "DELETE",
    });
  }

  subscribePush(input: PushSubscribeRequest): Promise<OkResponse> {
    return this.request<OkResponse>("/api/me/push", { method: "POST", json: input });
  }

  unsubscribePush(input: PushUnsubscribeRequest): Promise<OkResponse> {
    return this.request<OkResponse>("/api/me/push", { method: "DELETE", json: input });
  }

  /* ----------------------------------------------------------- directory */

  users(): Promise<UsersResponse> {
    return this.request<UsersResponse>("/api/users");
  }

  /* --------------------------------------------------------------- admin */

  adminInvites(): Promise<InvitesResponse> {
    return this.request<InvitesResponse>("/api/admin/invites");
  }

  createInvite(input: CreateInviteRequest): Promise<CreateInviteResponse> {
    return this.request<CreateInviteResponse>("/api/admin/invites", { method: "POST", json: input });
  }

  deleteInvite(inviteId: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/admin/invites/${encodeURIComponent(inviteId)}`, {
      method: "DELETE",
    });
  }

  /** A2: a single-use, expiring code that attaches a new device to an existing member. */
  createRecoveryCode(userId: string, input: CreateRecoveryRequest = {}): Promise<CreateRecoveryResponse> {
    return this.request<CreateRecoveryResponse>(
      `/api/admin/members/${encodeURIComponent(userId)}/recovery`,
      { method: "POST", json: input },
    );
  }

  adminMembers(): Promise<AdminMembersResponse> {
    return this.request<AdminMembersResponse>("/api/admin/members");
  }

  deleteMember(userId: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/admin/members/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    });
  }

  adminStats(): Promise<AdminStatsView> {
    return this.request<AdminStatsView>("/api/admin/stats");
  }

  /** §4: every user with the full `UserView`, optionally filtered by status. */
  adminUsers(status?: UserStatus): Promise<AdminUsersResponse> {
    return this.request<AdminUsersResponse>("/api/admin/users", { query: { status } });
  }

  /**
   * §4: approve, promote or provision one user. The api refuses a change to the caller's own
   * `status` or `isAdmin`; `409 email_taken` comes back as a flat error body.
   */
  adminUpdateUser(userId: string, input: AdminUserPatchRequest): Promise<UserResponse> {
    return this.request<UserResponse>(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      json: input,
    });
  }

  /* --------------------------------------------------------------- chats */

  chats(): Promise<ChatsResponse> {
    return this.request<ChatsResponse>("/api/chats");
  }

  createChat(input: CreateChatRequest): Promise<ChatResponse> {
    return this.request<ChatResponse>("/api/chats", { method: "POST", json: input });
  }

  chat(chatId: string): Promise<ChatDetailResponse> {
    return this.request<ChatDetailResponse>(`/api/chats/${encodeURIComponent(chatId)}`);
  }

  patchChat(chatId: string, input: PatchChatRequest): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/api/chats/${encodeURIComponent(chatId)}`, {
      method: "PATCH",
      json: input,
    });
  }

  muteChat(chatId: string, input: PatchChatMuteRequest): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/api/chats/${encodeURIComponent(chatId)}/mute`, {
      method: "PATCH",
      json: input,
    });
  }

  addChatMembers(chatId: string, input: AddChatMembersRequest): Promise<AddChatMembersResponse> {
    return this.request<AddChatMembersResponse>(`/api/chats/${encodeURIComponent(chatId)}/members`, {
      method: "POST",
      json: input,
    });
  }

  removeChatMember(chatId: string, userId: string): Promise<OkResponse> {
    return this.request<OkResponse>(
      `/api/chats/${encodeURIComponent(chatId)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }

  messages(chatId: string, query: MessagesQuery = {}): Promise<MessagesResponse> {
    return this.request<MessagesResponse>(`/api/chats/${encodeURIComponent(chatId)}/messages`, {
      query: { before: query.before, limit: query.limit },
    });
  }

  sendMessage(chatId: string, input: PostMessageRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/chats/${encodeURIComponent(chatId)}/messages`, {
      method: "POST",
      json: input,
    });
  }

  /** `POST /api/chats/:id/export` — W14, assemble this member's copy of the conversation. */
  exportChat(chatId: string): Promise<ExportResponse> {
    return this.request<ExportResponse>(`/api/chats/${encodeURIComponent(chatId)}/export`, {
      method: "POST",
    });
  }

  /** `PATCH /api/me/privacy` — W13, what the caller shares with the others. */
  setPrivacy(input: PrivacyRequest): Promise<MeResponse> {
    return this.request<MeResponse>("/api/me/privacy", { method: "PATCH", json: input });
  }

  /** `PATCH /api/chats/:id/disappearing` — W13, the chat's timer in seconds (`null` turns it off). */
  setDisappearing(chatId: string, seconds: number | null): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/api/chats/${encodeURIComponent(chatId)}/disappearing`, {
      method: "PATCH",
      json: { seconds },
    });
  }

  /** `PATCH /api/chats/:id/pin` — W5, the caller's own pin. */
  setChatPin(chatId: string, pinned: boolean): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/api/chats/${encodeURIComponent(chatId)}/pin`, {
      method: "PATCH",
      json: { pinned },
    });
  }

  /** `PATCH /api/chats/:id/pinned-message` — W5, the chat's one pinned message (`null` unpins). */
  setPinnedMessage(chatId: string, messageId: string | null): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/api/chats/${encodeURIComponent(chatId)}/pinned-message`, {
      method: "PATCH",
      json: { messageId },
    });
  }

  /** `PUT /api/messages/:id/vote` — W11, the caller's vote on one poll option (toggles). */
  vote(messageId: string, input: VoteRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/messages/${encodeURIComponent(messageId)}/vote`, {
      method: "PUT",
      json: input,
    });
  }

  /** `PUT /api/messages/:id/reaction` — W9, the caller's one reaction (a second replaces the first). */
  setReaction(messageId: string, input: SetReactionRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/messages/${encodeURIComponent(messageId)}/reaction`, {
      method: "PUT",
      json: input,
    });
  }

  /** `DELETE /api/messages/:id/reaction` — W9, clears the caller's own reaction. */
  clearReaction(messageId: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/messages/${encodeURIComponent(messageId)}/reaction`, {
      method: "DELETE",
    });
  }

  /** `POST /api/messages/forward` — copies into each target chat, media reused, marked forwarded. */
  forwardMessages(input: ForwardMessagesRequest): Promise<ForwardMessagesResponse> {
    return this.request<ForwardMessagesResponse>("/api/messages/forward", {
      method: "POST",
      json: input,
    });
  }

  /** `PATCH /api/messages/:id` — author only, `text` only, not after a delete (§4). */
  editMessage(messageId: string, input: EditMessageRequest): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/messages/${encodeURIComponent(messageId)}`, {
      method: "PATCH",
      json: input,
    });
  }

  /**
   * `GET /api/search` (W3) — an indexed scan over `messages.body` in every chat the caller is a
   * member of, newest first. `chatId` narrows it to one chat.
   */
  search(query: SearchQuery): Promise<SearchResponse> {
    return this.request<SearchResponse>("/api/search", {
      query: { q: query.q, chatId: query.chatId, limit: query.limit },
    });
  }

  /** `DELETE /api/messages/:id` — delete-for-everyone: the row stays as a tombstone (§4). */
  deleteMessage(messageId: string): Promise<MessageResponse> {
    return this.request<MessageResponse>(`/api/messages/${encodeURIComponent(messageId)}`, {
      method: "DELETE",
    });
  }

  uploadChatMedia(chatId: string, file: Blob, filename?: string): Promise<MediaResponse> {
    return this.#upload<MediaResponse>(
      `/api/chats/${encodeURIComponent(chatId)}/media`,
      file,
      filename,
    );
  }

  markRead(chatId: string, input: ReadRequest): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/chats/${encodeURIComponent(chatId)}/read`, {
      method: "POST",
      json: input,
    });
  }

  /** `GET /api/calls` — W6, the caller's call log across their chats, newest first. */
  callLog(limit?: number): Promise<CallLogResponse> {
    return this.request<CallLogResponse>("/api/calls", { query: { limit } });
  }

  chatCalls(chatId: string): Promise<CallsResponse> {
    return this.request<CallsResponse>(`/api/chats/${encodeURIComponent(chatId)}/calls`);
  }

  /* --------------------------------------------------------------- calls */

  startCall(chatId: string, input: StartCallRequest): Promise<CallTokenResponse> {
    return this.request<CallTokenResponse>(`/api/chats/${encodeURIComponent(chatId)}/calls`, {
      method: "POST",
      json: input,
    });
  }

  joinCall(callId: string): Promise<CallTokenResponse> {
    return this.request<CallTokenResponse>(`/api/calls/${encodeURIComponent(callId)}/join`, {
      method: "POST",
    });
  }

  /**
   * Rings additional chat members into a call that is already live (CONTRACT §4). The ring reaches
   * the chosen members' live sockets and, for those without one, their push subscriptions — the
   * same two transports as the call's first ring.
   */
  ringCallMembers(callId: string, input: RingCallRequest): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/calls/${encodeURIComponent(callId)}/ring`, {
      method: "POST",
      json: input,
    });
  }

  declineCall(callId: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/calls/${encodeURIComponent(callId)}/decline`, {
      method: "POST",
    });
  }

  endCall(callId: string): Promise<OkResponse> {
    return this.request<OkResponse>(`/api/calls/${encodeURIComponent(callId)}/end`, {
      method: "POST",
    });
  }

  call(callId: string): Promise<CallDetailResponse> {
    return this.request<CallDetailResponse>(`/api/calls/${encodeURIComponent(callId)}`);
  }
}
